// Workspace App — Full admin user table (list + inline edit + actions).
// Mirrors MarkovLab admin panel pattern but with Role AND Tier editable
// inline per row (fixing the gap the user flagged).
import React from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, UserPlus, KeyRound, LogOut as RevokeIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TIERS = ["free", "trial", "pro", "beta"];
const ROLES = ["user", "admin"];
const STATUS_COLOR = {
  active: "#10b981", pending_approval: "#f59e0b", rejected: "#ef4444",
};

export default function WorkspaceAdminUsersPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showCreate, setShowCreate] = React.useState(false);
  const [pwEditingId, setPwEditingId] = React.useState(null);
  const [pwValue, setPwValue] = React.useState("");
  const [form, setForm] = React.useState({
    email: "", name: "", role: "user", tier: "free", password: "",
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workspace/admin/users");
      setRows(data.users || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email) { toast.error("Email required"); return; }
    try {
      await api.post("/workspace/admin/users", form);
      toast.success("User created");
      setShowCreate(false);
      setForm({ email: "", name: "", role: "user", tier: "free", password: "" });
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Create failed";
      toast.error(typeof msg === "string" ? msg : "Create failed");
    }
  };

  const patchField = async (uid, field, value) => {
    try {
      await api.patch(`/workspace/admin/users/${uid}`, { [field]: value });
      // Optimistic update.
      setRows(rs => rs.map(r => r.id === uid ? { ...r, [field]: value } : r));
    } catch { toast.error("Update failed"); load(); }
  };

  const setPassword = async (uid) => {
    if (pwValue.length < 8) { toast.error("Min 8 chars"); return; }
    try {
      await api.post(`/workspace/admin/users/${uid}/set-password`, { password: pwValue });
      toast.success("Password set");
      setPwEditingId(null); setPwValue("");
      load();
    } catch { toast.error("Password set failed"); }
  };
  const revokeGoogle = async (uid) => {
    if (!window.confirm("Revoke this user's Google link? They'll need email/password to sign in.")) return;
    try {
      await api.post(`/workspace/admin/users/${uid}/revoke-google`);
      toast.success("Google revoked");
      load();
    } catch { toast.error("Revoke failed"); }
  };
  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Delete ${email}? This can't be undone.`)) return;
    try {
      await api.delete(`/workspace/admin/users/${uid}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="workspace-admin-users">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Users <span className="text-sm font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>({rows.length})</span>
        </h1>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            data-testid="workspace-users-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(!showCreate)}
            data-testid="workspace-users-create-toggle">
            <UserPlus className="w-3 h-3 mr-1" />Add User
          </Button>
        </div>
      </div>

      {showCreate && (
        <div className="border rounded-sm p-3 mb-4 grid grid-cols-2 gap-2"
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
          data-testid="workspace-users-create-form">
          <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            className="h-8 text-xs" data-testid="create-email" />
          <Input placeholder="Name (optional)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="h-8 text-xs" data-testid="create-name" />
          <select className="h-8 text-xs border rounded-sm px-2"
            style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
            value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            data-testid="create-role">
            {ROLES.map(r => <option key={r} value={r}>Role: {r}</option>)}
          </select>
          <select className="h-8 text-xs border rounded-sm px-2"
            style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
            value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}
            data-testid="create-tier">
            {TIERS.map(t => <option key={t} value={t}>Tier: {t}</option>)}
          </select>
          <Input placeholder="Password (optional — for email login)" type="password"
            value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            className="h-8 text-xs col-span-2" data-testid="create-password" />
          <div className="col-span-2 flex justify-end">
            <Button size="sm" onClick={create} data-testid="create-submit">Create</Button>
          </div>
        </div>
      )}

      <div className="border rounded-sm overflow-x-auto"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              {["Email", "Name", "Role", "Tier", "Auth", "Password", "Status", "Actions"].map(h => (
                <th key={h} className="text-left p-2 font-medium text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.id} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}
                data-testid={`user-row-${u.email}`}>
                <td className="p-2 font-mono-data">{u.email}</td>
                <td className="p-2">{u.name || "—"}</td>
                <td className="p-2">
                  <select
                    className="h-6 text-[10px] border rounded-sm px-1"
                    style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                    value={u.role}
                    onChange={e => patchField(u.id, "role", e.target.value)}
                    data-testid={`role-select-${u.email}`}
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-2">
                  <select
                    className="h-6 text-[10px] border rounded-sm px-1"
                    style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                    value={u.tier}
                    onChange={e => patchField(u.id, "tier", e.target.value)}
                    data-testid={`tier-select-${u.email}`}
                  >
                    {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className="p-2 text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>{u.auth_method}</td>
                <td className="p-2 text-[10px]">{u.password_set ? "✓ set" : "—"}</td>
                <td className="p-2">
                  <span className="text-[9px] px-1.5 py-0.5 rounded-sm"
                    style={{
                      color: STATUS_COLOR[u.status] || "var(--hoip-text-tertiary)",
                      background: `${STATUS_COLOR[u.status] || "#71717A"}15`,
                    }}
                    data-testid={`user-status-${u.email}`}>
                    {u.status}
                  </span>
                </td>
                <td className="p-2 whitespace-nowrap">
                  {pwEditingId === u.id ? (
                    <>
                      <Input type="password" className="h-6 text-[10px] w-32 inline-block mr-1"
                        placeholder="New password" value={pwValue}
                        onChange={e => setPwValue(e.target.value)}
                        data-testid={`pw-input-${u.email}`} />
                      <Button size="sm" className="h-6 text-[9px] mr-1"
                        onClick={() => setPassword(u.id)}
                        data-testid={`pw-save-${u.email}`}>Save</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[9px]"
                        onClick={() => { setPwEditingId(null); setPwValue(""); }}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" className="h-6 text-[9px] mr-1"
                        title="Set password" onClick={() => setPwEditingId(u.id)}
                        data-testid={`pw-btn-${u.email}`}>
                        <KeyRound className="w-3 h-3" />
                      </Button>
                      {u.google_id && (
                        <Button size="sm" variant="outline" className="h-6 text-[9px] mr-1"
                          title="Revoke Google" onClick={() => revokeGoogle(u.id)}
                          data-testid={`revoke-google-btn-${u.email}`}>
                          <RevokeIcon className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-6 text-[9px] text-red-500"
                        title="Delete" onClick={() => deleteUser(u.id, u.email)}
                        data-testid={`delete-btn-${u.email}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center" style={{ color: "var(--hoip-text-tertiary)" }}>
                No users yet — click &quot;Add User&quot; to seed one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
